import type { ScenarioContent } from '../types';

/**
 * Venue and industrial scenario pages. Performance figures come from the
 * published specifications; installation notes describe generic practice and
 * are framed as guidance to confirm with a qualified electrician.
 */
export const venueScenarios: ScenarioContent[] = [
  {
    slug: 'places-of-worship-event-halls',
    nav: 'Halls & worship spaces',
    title: 'Heating High-Ceiling Halls and Event Spaces',
    description:
      'Radiant heating for halls, worship spaces and event venues with high ceilings: why convection fails, how to size by occupied area, and mounting on columns or ceilings.',
    h1: 'Heating high-ceiling halls without heating the roof space',
    lede:
      'In a hall with an eight-metre ceiling, warm air is a waste product. Radiant heat puts the energy where the people are.',
    answer:
      'For halls and event spaces, size on occupied floor area rather than room volume: roughly 80–100 W per m² of seating under a sheltered roof, rising to 130 W per m² where large doors stand open. Mount at 1.8–2.5 m on columns or perimeter walls, or use ceiling units where columns block the sightline.',
    sections: [
      {
        heading: 'Why convection heating wastes a hall',
        paragraphs: [
          'Air heated at floor level rises. In a hall with a high ceiling, that means the warmest air collects under the roof while the seating remains cool — the classic stratification problem. The taller the space and the more intermittent the use, the worse the economics: a congregation that occupies the hall for two hours a week pays to heat the full volume every time.',
          'Radiant panels behave differently. The energy is emitted directionally and absorbed by people, pews, seats and the floor. Air temperature can stay several degrees lower while occupants feel comfortable, because comfort in a large space is driven by radiant exchange with surrounding surfaces, not by air temperature alone.',
        ],
      },
      {
        heading: 'Layout considerations for halls',
        bullets: [
          'Heat the seating, not the aisles. Radiant heat aimed at circulation space is wasted output.',
          'Mount on columns or perimeter walls at 1.8–2.5 m where the seating faces them; use ceiling-mounted units where columns interrupt the throw or where wall space is listed or fragile.',
          'Zone by seating block so a midweek gathering of forty people does not require the full hall to be energised.',
          'For intermittent use, prefer radiant for its instant response — there is no warm-up period to schedule around.',
          'In heritage buildings, surface-mounting avoids penetrative work on historic fabric; confirm fixing method with the building surveyor.',
        ],
      },
      {
        heading: 'Output and supply for large halls',
        paragraphs: [
          'Our 4.0 kW model covers up to 50 m² under sheltered conditions and requires a 380 V supply — in a hall this is often the efficient choice because it reduces the number of units and fixing points. Where three-phase is unavailable, multiple 3.2 kW units at 220–240 V (13.9 A each) achieve the same total output across more circuits.',
          'For a 200 m² hall, a workable starting point is six to eight 3.2 kW units arranged along the seating perimeter, zoned into two or three circuits. That is a planning estimate, not a specification: ceiling height, glazing, door openings and occupancy pattern all move the number.',
        ],
      },
    ],
    sizing: [
      { space: 'Small chapel or meeting room', area: '30 m²', power: '2.4 kW', model: '13C 2.4 kW', note: 'Two units on opposing walls for even radiant coverage.' },
      { space: 'Community hall, standard ceiling', area: '50 m²', power: '4.0 kW', model: '14A 4.0 kW (380 V)', note: 'Single high-output unit, or two 2.2 kW units.' },
      { space: 'Worship space, high ceiling', area: '80–100 m²', power: '3.2 kW × 3', model: '13C / 22B 3.2 kW', note: 'Column mounting at 2.0–2.5 m facing the seating.' },
      { space: 'Event hall with large openings', area: '150 m²', power: '3.2 kW × 6', model: '22B 3.2 kW', note: 'Higher surface temperature compensates for door draughts.' },
      { space: 'Large hall, 200 m²+', area: '200 m²+', power: '4.0 kW × 5–6', model: '14A 4.0 kW (380 V)', note: 'Engineered layout, zoned circuits, phased installation.' },
    ],
    sizingNote:
      'Radiant heating is sized on the occupied area and the distance to the radiant face, not on room volume. For halls with ceilings above 4 m, send a section drawing: mounting height and the angle of the radiant face change the recommended output materially.',
    recommended: [
      {
        name: '14A Series — 4.0 kW option',
        href: '/product/infrared-heaters/14a-series',
        why: 'The highest single-unit output in our wall range, rated to 50 m² sheltered — fewer units, fewer fixing points in a large or heritage space.',
      },
      {
        name: '13C Series — wide radiant face',
        href: '/product/infrared-heaters/13c-series',
        why: 'A wide panel gives an even wash across a seating block instead of a hot spot under one unit.',
      },
      {
        name: 'JHD028-B ceiling heater — 2.0 kW',
        href: '/product/portable-heaters/jhd028-b',
        why: 'Ceiling mounting where columns or historic walls cannot take a wall bracket.',
      },
    ],
    installs: [
      { image: '/image/cases/case-timber-restaurant.webp', caption: 'High-ceiling interior with perimeter radiant mounting.' },
      { image: '/image/cases/vineyard-wide.webp', caption: 'Large-span interior space, radiant heat on the occupied zone.' },
      { image: '/image/cases/covered-lounge.webp', caption: 'Grouped seating under a permanent roof structure.' },
    ],
    specHighlights: [
      { label: 'Maximum output per unit', value: '4.0 kW (13A / 14A)' },
      { label: 'Rated coverage', value: 'Up to 50 m² per unit, sheltered' },
      { label: 'Supply', value: '220–240 V standard; 380 V for 4.0 kW' },
      { label: 'Surface temperature', value: '300 °C (13/14 series) / 380 °C (22B)' },
      { label: 'Mounting height', value: '1.8 – 2.5 m; ceiling units above head height' },
      { label: 'Control', value: 'Per-zone switching or thermostat' },
    ],
    faqs: [
      {
        q: 'How do I heat a hall with a very high ceiling?',
        a: 'Heat the occupied zone rather than the volume. Mount radiant panels at 1.8–2.5 m on columns or perimeter walls, or use ceiling units directly above the seating, and zone the hall by seating block. This keeps energy at occupant level instead of stratifying under the roof.',
      },
      {
        q: 'Is radiant heating suitable for intermittent use, like a weekly service?',
        a: 'Yes — it is the main reason halls choose radiant. There is no boiler to bring up to temperature: occupants feel warmth within seconds of switch-on, so a two-hour weekly gathering does not require hours of pre-heating.',
      },
      {
        q: 'Can heaters be mounted in a heritage or listed building?',
        a: 'Surface mounting on columns or walls avoids penetrative work on historic fabric, and ceiling suspension is possible from existing structural beams. We supply dimensional drawings and bracket details so your conservation officer or surveyor can review the fixing method before work starts.',
      },
      {
        q: 'Do I need three-phase power for a large hall?',
        a: 'A 4.0 kW unit requires 380 V, which is normally available in a hall of that size. If only single-phase is available, multiple 3.2 kW units at 13.9 A each provide the same total output, distributed across more circuits.',
      },
      {
        q: 'How many heaters for a 200 m² hall?',
        a: 'A planning estimate is six to eight 3.2 kW units along the seating perimeter, split into two or three circuits, or five to six 4.0 kW units on three-phase. Ceiling height, glazing and door openings can shift that by 20–30%, so we recommend confirming against a plan.',
      },
    ],
    related: [
      { label: 'Warehouses and workstations', href: '/business/warehouse-workstations' },
      { label: 'Hotel terraces and guest areas', href: '/business/hotels-terraces' },
      { label: 'Sizing calculator', href: '/tools/heater-sizing-calculator' },
      { label: 'Send us your floor plan', href: '/contact' },
    ],
  },
  {
    slug: 'warehouse-workstations',
    nav: 'Warehouses & workstations',
    title: 'Warehouse & Workstation Spot Heating',
    description:
      'Spot heating for warehouses, loading bays and workshops: heat people at fixed workstations instead of the building volume, with mounting, safety and sizing guidance.',
    h1: 'Warehouse heating that warms the workstation, not the warehouse',
    lede:
      'Heating a 4,000 m² distribution centre to comfort temperature is unaffordable. Heating eight workstations is not.',
    answer:
      'For warehouses and workshops, use spot heating: mount radiant panels 1.8–2.5 m above or beside each fixed workstation and size at 1.0–2.4 kW per position. Radiant heat does not depend on holding air temperature, so an unheated building shell is not a barrier — but air movement from doors and forklifts must be managed.',
    sections: [
      {
        heading: 'The economics of spot heating',
        paragraphs: [
          'A warehouse is thermally leaky by design: roller doors, loading bays, high roofs and constant vehicle movement. Conditioning that volume is not a realistic target, and most operators accept a cold shell with local comfort at the positions where people actually stand still.',
          'Radiant spot heating matches that operating model. A 1.8 kW panel above a packing bench delivers comfort to the operator within seconds, regardless of whether the surrounding air is 8 °C or 14 °C, because the energy is absorbed by the person and the bench rather than the air. Energy use scales with the number of positions in use, not with building volume.',
        ],
      },
      {
        heading: 'Where to mount in an industrial space',
        bullets: [
          'Directly above a fixed workstation at 1.8–2.5 m, or on a nearby column angled toward the operator — not aimed across an open aisle.',
          'Keep units clear of forklift and pallet-truck traffic; where vehicles pass beneath, use ceiling mounting with a minimum clearance agreed with your safety officer.',
          'Avoid positions in the direct path of loading-bay doors, where cold air movement will strip the radiant layer.',
          'Use IPX4 units in wash-down or damp areas; confirm the enclosure rating against your cleaning regime.',
          'Provide local switching at each position so heat runs only when the bench is manned.',
        ],
      },
      {
        heading: 'What radiant does not do',
        paragraphs: [
          'Radiant heating does not dry a damp building, prevent condensation on stored stock, or protect water pipes from frost. Where those outcomes matter, radiant is a comfort measure layered on top of a building fabric and ventilation strategy — not a substitute for one. We would rather say that plainly than sell heaters as building services.',
        ],
      },
    ],
    sizing: [
      { space: 'Packing bench or inspection station', area: '1 workstation', power: '1.0–1.8 kW', model: '13A / 13B 1.0–1.8 kW', note: 'Mounted above and slightly in front of the operator.' },
      { space: 'Assembly cell, 2–3 operators', area: '10–15 m²', power: '2.4 kW', model: '13C 2.4 kW', note: 'One unit per cell, switched locally.' },
      { space: 'Loading bay desk', area: '10 m²', power: '2.2 kW', model: '22B 2.2 kW', note: 'Higher surface temperature for draughty bay openings.' },
      { space: 'Workshop bay, open front', area: '20–30 m²', power: '3.2 kW', model: '22B 3.2 kW', note: 'Aim away from the open door; add a screen if possible.' },
      { space: 'Cold store anteroom', area: '10 m²', power: '2.4 kW', model: '13C 2.4 kW', note: 'Comfort at the doorway; not a frost-protection system.' },
    ],
    sizingNote:
      'Spot heating is sized per position, not per square metre of building. Give us the number of fixed positions and their layout — a workstation plan produces a far more accurate quotation than a floor area.',
    recommended: [
      {
        name: '13B Series — 1.0 to 3.2 kW',
        href: '/product/infrared-heaters/13b-series',
        why: 'A compact panel that fits between racking and ceiling services at typical workstation heights.',
      },
      {
        name: '22B Series — 380 °C surface',
        href: '/product/infrared-heaters/22b-series',
        why: 'Higher surface temperature holds comfort where doors open frequently and air movement is unavoidable.',
      },
      {
        name: 'JHG019-B wall-mounted patio heater — 2.0 kW',
        href: '/product/portable-heaters/jhg019-b',
        why: 'A plug-in wall unit for positions that need heat before a fixed electrical installation is signed off.',
      },
    ],
    installs: [
      { image: '/image/cases/case-workshop.webp', caption: 'Workshop bay with radiant units above the bench line.' },
      { image: '/image/cases/case-garage.webp', caption: 'Service bay spot heating at 13A mounting height.' },
      { image: '/image/cases/greenhouse.webp', caption: 'Large-span working space with localised radiant coverage.' },
    ],
    specHighlights: [
      { label: 'Typical output per position', value: '1.0 – 2.4 kW' },
      { label: 'Mounting height', value: '1.8 – 2.5 m above the position' },
      { label: 'Ingress protection', value: 'IPX4 splash protection' },
      { label: 'Response time', value: 'Warmth perceived within seconds' },
      { label: 'Control', value: 'Local switch or per-zone thermostat' },
      { label: 'Supply', value: '220–240 V 50/60 Hz' },
    ],
    faqs: [
      {
        q: 'Can infrared heaters heat an entire warehouse?',
        a: 'Not economically, and we would not recommend trying. Radiant heat is a spot solution: it keeps people comfortable at fixed positions while the building shell stays cold. Heating a full distribution centre requires a building fabric and ventilation strategy first.',
      },
      {
        q: 'Will radiant heaters stop condensation on stored stock?',
        a: 'No. Radiant panels warm people and surfaces in the radiant path, not the air or the whole building envelope. Condensation control needs ventilation, insulation and dew-point management. We are explicit about this because mis-specified radiant heating leads to disappointed buyers.',
      },
      {
        q: 'How close can a heater be to a workstation?',
        a: 'Our recommended mounting band is 1.8–2.5 m above floor level. Closer than that, the radiant intensity at head height becomes uncomfortable; further away, output is wasted. For seated operators, mounting slightly in front of rather than directly overhead avoids heating the top of the head only.',
      },
      {
        q: 'Are the heaters safe around forklifts and moving stock?',
        a: 'Wall or column mounting above vehicle height is the safe arrangement. Where a unit must sit over an aisle, agree clearance with your safety officer and protect the cable route; IPX4 covers splash, not impact, so physical protection matters in vehicle areas.',
      },
      {
        q: 'Can I install them myself in a workshop?',
        a: 'The electrical work should be carried out by a qualified electrician: a 2.4 kW unit draws 10.4 A at 220–240 V and needs a properly protected circuit. Mounting and aiming are straightforward, and we supply bracket drawings and the load schedule for your electrician.',
      },
    ],
    related: [
      { label: 'Halls and worship spaces', href: '/business/places-of-worship-event-halls' },
      { label: 'Coastal and covered terraces', href: '/business/covered-terraces-coastal' },
      { label: 'Sizing calculator', href: '/tools/heater-sizing-calculator' },
      { label: 'Ask for a workstation plan', href: '/contact' },
    ],
  },
  {
    slug: 'covered-terraces-coastal',
    nav: 'Coastal terraces',
    title: 'Coastal Terrace Heating: IPX4 and Salt Air',
    description:
      'Heating covered terraces in coastal environments: what IPX4 covers, what salt air does to heaters, mounting under canopies, and a maintenance checklist.',
    h1: 'Coastal terrace heating: what IPX4 does and does not protect against',
    lede:
      'Salt air is the hardest environment for any outdoor electrical product. The honest answer is a specification and maintenance regime, not a miracle coating.',
    answer:
      'For coastal terraces, specify IPX4 heaters, mount them under a canopy or soffit so rain and direct spray do not reach the unit, use stainless fasteners, and plan a rinse-and-inspect routine at the start and end of each season. IPX4 covers rain splash; it is not a salt-air or corrosion rating.',
    sections: [
      {
        heading: 'What IPX4 actually means',
        paragraphs: [
          'IPX4 means protection against water splashing from any direction. It is the appropriate rating for a heater installed outdoors under a canopy, where rain can be driven onto the casing. It does not mean the unit can be immersed, pressure-washed, or left unprotected in direct sea spray.',
          'This distinction matters most on coastal sites, where buyers often assume an outdoor rating covers salt. It does not: IP ratings address water ingress, while salt resistance depends on housing material, coating integrity and fastener grade. We state this explicitly because a heater that fails in eighteen months on a seafront does nobody any good.',
        ],
      },
      {
        heading: 'Specifying for salt exposure',
        bullets: [
          'Mount under a canopy, soffit or pergola beam so the unit is shielded from direct rain and spray; the radiant face should still see the seating.',
          'Use stainless steel fasteners and brackets — the fixing, not the heater, is usually the first thing to corrode.',
          'Keep the powder-coated casing intact; touch in any chip promptly, because corrosion starts at coating damage.',
          'Avoid mounting directly above the seaward edge of a balcony, where wind-driven spray reaches the unit.',
          'Rinse the casing with fresh water and inspect terminals at seasonal intervals — quarterly in exposed locations.',
          'Confirm the circuit has RCD protection, particularly where salt deposits can bridge insulation over time.',
        ],
      },
      {
        heading: 'Layout on a covered coastal terrace',
        paragraphs: [
          'On a covered terrace the canopy does most of the work: it blocks rain, reduces wind speed and improves the effective coverage of each heater. That means a 2.2 kW unit which holds 30 m² in still air can perform close to its rated coverage under a good canopy, even with the sea 30 m away.',
          'Where the terrace is only partly covered, treat the covered portion as the comfort zone and place heaters at its inland edge facing outward, so guests sit inside the radiant field rather than at its boundary. Windbreaks — glass screens or planters — reduce both discomfort and salt deposition on the heaters themselves.',
        ],
      },
    ],
    sizing: [
      { space: 'Covered balcony, sheltered from spray', area: '10 m²', power: '1.0 kW', model: '13W 1.0 kW', note: 'Single unit under the soffit.' },
      { space: 'Pergola terrace, sea 50 m+ away', area: '20 m²', power: '1.8 kW', model: '13A / 13C 1.8 kW', note: 'Beam mounting, stainless fixings.' },
      { space: 'Beachfront terrace with side screens', area: '30 m²', power: '2.2 kW × 2', model: '22B 2.2 kW', note: 'Screens reduce spray and improve coverage.' },
      { space: 'Sea-facing restaurant terrace', area: '40 m²', power: '3.2 kW × 2', model: '22B 3.2 kW', note: 'Keep units back from the seaward edge.' },
      { space: 'Exposed deck, direct spray', area: '—', power: 'Not recommended', model: 'Relocate or build a canopy', note: 'IPX4 is not rated for direct, sustained spray.' },
    ],
    sizingNote:
      'On coastal sites the canopy, not the heater, determines success. If the unit cannot be shielded from direct spray, the correct answer is to change the installation rather than to buy a higher rating — our range is IPX4, and we will say so.',
    recommended: [
      {
        name: '13A Series — 1.0 to 4.0 kW',
        href: '/product/infrared-heaters/13a-series',
        why: 'A proven wall panel with IPX4 protection and a simple, sealed casing that is easy to inspect and clean seasonally.',
      },
      {
        name: '22B Series — 1.6 to 3.2 kW',
        href: '/product/infrared-heaters/22b-series',
        why: 'Higher surface temperature holds comfort on a breezy waterfront where a 300 °C panel loses throw.',
      },
      {
        name: '13W Series — WiFi control',
        href: '/product/infrared-heaters/13w-series',
        why: 'Lets a coastal venue switch zones remotely and monitor use without staff walking the terrace in bad weather.',
      },
    ],
    installs: [
      { image: '/image/cases/ocean-patio.webp', caption: 'Ocean-side patio with sheltered mounting positions.' },
      { image: '/image/cases/covered-lounge.webp', caption: 'Covered lounge under a permanent canopy — the arrangement coastal sites should aim for.' },
      { image: '/image/cases/garden-balcony.webp', caption: 'Balcony installation with the unit protected by the soffit above.' },
    ],
    specHighlights: [
      { label: 'Ingress protection', value: 'IPX4 — splash from any direction' },
      { label: 'Not rated for', value: 'Immersion, pressure washing, sustained sea spray' },
      { label: 'Housing', value: 'Powder-coated casing; stainless fasteners recommended' },
      { label: 'Mounting', value: 'Under canopy or soffit, 1.8–2.5 m' },
      { label: 'Maintenance', value: 'Fresh-water rinse and terminal inspection, quarterly in exposed sites' },
      { label: 'Circuit protection', value: 'RCD required' },
    ],
    faqs: [
      {
        q: 'Are IPX4 heaters suitable for coastal areas?',
        a: 'IPX4 units are suitable where they are shielded from direct spray — under a canopy or soffit. IPX4 addresses water splash, not salt corrosion, so coastal installations also need stainless fasteners, intact coating and a seasonal rinse-and-inspect routine.',
      },
      {
        q: 'Will salt air corrode the heater?',
        a: 'Salt accelerates corrosion on any metal, including powder-coated steel. The practical controls are shielding, intact coating, stainless fixings and periodic rinsing. We would rather set that expectation than claim a marine rating our range does not carry.',
      },
      {
        q: 'Can I mount a heater directly above the sea edge?',
        a: 'We would advise against it. Sustained wind-driven spray exceeds IPX4, and the unit will be the first thing to fail. Move the heater inland, add a screen, or install a canopy so the unit is protected while still facing the seating.',
      },
      {
        q: 'How often should coastal heaters be maintained?',
        a: 'Inspect and rinse quarterly on exposed sites, and at the start and end of each heating season as a minimum. Check terminals for salt deposits, confirm the RCD trips, and touch in any coating damage before it becomes corrosion.',
      },
      {
        q: 'Do you supply stainless steel brackets?',
        a: 'We can supply stainless fasteners and brackets as part of the order — specify the coastal requirement when requesting a quotation so the correct grade is included rather than substituted on site.',
      },
    ],
    related: [
      { label: 'Rooftop bars', href: '/business/rooftop-bars' },
      { label: 'Hotel terraces and guest areas', href: '/business/hotels-terraces' },
      { label: 'Compliance and certifications', href: '/compliance' },
      { label: 'Talk to our team about coastal specs', href: '/contact' },
    ],
  },
];
